import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Put a new page at the top -- unless the url names somewhere else on it.
 *
 * <p>A single-page app does not reload, so nothing resets the scroll position
 * on a route change: follow a link from halfway down a long page and the next
 * one opens halfway down. That is what this is for.
 *
 * <p><b>It threw the HASH away until 2026-09-16</b>, and the symptom was
 * confusing because it depended on where you started. The nav bar's Roadmap
 * entry is an anchor at `/#roadmap`, and the marketing sections it points at
 * live on `/`:
 *
 * <ul>
 *   <li>From the home page, the pathname does not change, so the effect below
 *       never re-runs and the BROWSER's own anchor scrolling does the work. It
 *       looked fine.</li>
 *   <li>From anywhere else -- the welcome page, the codex, the console -- the
 *       pathname changes, this fires, and `scrollTo( 0, 0 )` lands on the top
 *       of the home page having discarded the hash the reader clicked.</li>
 * </ul>
 *
 * <p>So the hash is honoured first and the top is the fallback, which is the
 * behaviour a reader expects from a url that names a section.
 *
 * <p><b>A target's own scroll-margin-top wins over the bar's height.</b>
 * `themeNav` makes the header fixed once the page has scrolled, so an element
 * scrolled flush to the top of the viewport sits UNDERNEATH it. The home
 * sections, codex headings, and changelog releases already say how far below
 * the bar they land, in CSS, and the browser's own jump obeys it. This effect
 * fires on every hash change, after that jump, and it used to subtract the
 * bar's height instead -- so a click on /#features from the home page landed
 * right and was then carried 76px further down (Tim, 2026-10-14: "make space
 * above features same as roadmap when clicking anchor"; Roadmap only looked
 * right because its hash was already in the url). The bar's height is the
 * fallback for a target with no margin of its own.
 *
 * <p>Motion is a preference; see EsScrollIntoView, which makes the same
 * decision for form errors and explains it.
 */
export default function EsScrollToTop() {
    const { pathname, hash } = useLocation();

    useEffect( () => {
        const aStill = window.matchMedia
            && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
        const aBehavior = aStill ? 'auto' : 'smooth';

        // decodeURIComponent because a hash may be escaped on the way through
        // the address bar, and getElementById wants the real id.
        const aId = hash ? decodeURIComponent( hash.slice( 1 ) ) : '';
        const aTarget = aId ? document.getElementById( aId ) : null;

        if ( aTarget ) {
            const aMargin = parseFloat( getComputedStyle( aTarget ).scrollMarginTop ) || 0;
            const aNav = document.querySelector( '[data-testid="es-nav-bar"]' );
            const aOffset = aMargin || ( aNav ? aNav.offsetHeight : 0 );

            window.scrollTo( {
                top: aTarget.getBoundingClientRect().top + window.scrollY - aOffset,
                behavior: aBehavior,
            } );
            return;
        }

        // No hash, or a hash naming nothing on this page. The second happens
        // legitimately -- a stale bookmark, or a section that has moved -- and
        // the top of the page is the right answer to it rather than staying
        // wherever the previous page happened to be.
        window.scrollTo( 0, 0 );
    }, [ pathname, hash ] );

    return null;
}
